import { Component, signal } from '@angular/core';

export interface FaqItem {
  readonly question: string;
  readonly answer: string;
}

export interface BenefitItem {
  readonly iconPath: string;
  readonly label: string;
}

@Component({
  selector: 'app-faq-section',
  standalone: true,
  imports: [],
  templateUrl: './faq-section.html',
  styleUrl: './faq-section.scss',
})
export class FaqSection {
  readonly badgeIconPath = '/icons/question-mark.svg';

  readonly benefits: readonly BenefitItem[] = [
    {
      iconPath: '/icons/search-check.svg',
      label: 'Find datasets, models and concepts faster',
    },
    {
      iconPath: '/icons/shield.svg',
      label: 'Understand permissions and governed access clearly',
    },
    {
      iconPath: '/icons/chat.svg',
      label: 'Learn how the platform supports collaboration and innovation',
    },
  ];

  readonly faqs: readonly FaqItem[] = [
    {
      question: 'What can I discover on IUDX Healthcare?',
      answer:
        'You can browse datasets, reusable models, healthcare use cases, concepts, challenges, discussions and supporting resources through one unified healthcare-focused experience.',
    },
    {
      question: 'Who is this platform designed for?',
      answer:
        'The platform supports hospitals, providers, researchers, public health teams, innovators and developers looking to collaborate through governed healthcare data and models.',
    },
    {
      question: 'Can I search across all content types from the homepage?',
      answer:
        'Yes. The homepage includes a global search pattern intended to support cross-platform discovery across datasets, models, usecases, concepts and resources.',
    },
    {
      question: 'How do datasets and models differ on the platform?',
      answer:
        'Datasets provide governed healthcare data assets, while models provide reusable analytical or AI workflows that can be explored, benchmarked and applied in relevant healthcare scenarios.',
    },
    {
      question: 'Does the platform support secure and trusted access?',
      answer:
        'Yes. The experience is designed around secure discovery, role-aware access and trusted institutional workflows appropriate for healthcare data exchange ecosystems.',
    },
  ];

  /** First FAQ open by default; multiple indices may be open at once. */
  private readonly expanded = signal<ReadonlySet<number>>(new Set<number>([0]));

  toggle(index: number): void {
    this.expanded.update((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  isOpen(index: number): boolean {
    return this.expanded().has(index);
  }
}
